/**
 * Utility functions for managing 3-month periods for exams and questionnaires
 */

export interface Period {
  id: string;
  startDate: Date;
  endDate: Date;
  quarter: number;
  year: number;
}

/**
 * Get the current 3-month period
 */
export function getCurrentPeriod(): Period {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based
  
  // Determine quarter (0-based)
  const quarter = Math.floor(month / 3);
  
  // Calculate start and end dates
  const startMonth = quarter * 3;
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, startMonth + 3, 0); // Last day of the quarter
  
  const periodId = `${year}-Q${quarter + 1}`;
  
  return {
    id: periodId,
    startDate,
    endDate,
    quarter: quarter + 1, // 1-based for display
    year
  };
}

/**
 * Get period for a specific date
 */
export function getPeriodForDate(date: Date): Period {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  
  // Determine quarter (0-based)
  const quarter = Math.floor(month / 3);
  
  // Calculate start and end dates
  const startMonth = quarter * 3;
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, startMonth + 3, 0); // Last day of the quarter
  
  const periodId = `${year}-Q${quarter + 1}`;
  
  return {
    id: periodId,
    startDate,
    endDate,
    quarter: quarter + 1, // 1-based for display
    year
  };
}

/**
 * Check if a date is in the current period
 */
export function isInCurrentPeriod(date: Date): boolean {
  const currentPeriod = getCurrentPeriod();
  return date >= currentPeriod.startDate && date <= currentPeriod.endDate;
}

/**
 * Get all periods for a user (for historical view)
 */
export function getAllPeriodsForYear(year: number): Period[] {
  const periods: Period[] = [];
  
  for (let quarter = 0; quarter < 4; quarter++) {
    const startMonth = quarter * 3;
    const startDate = new Date(year, startMonth, 1);
    const endDate = new Date(year, startMonth + 3, 0);
    const periodId = `${year}-Q${quarter + 1}`;
    
    periods.push({
      id: periodId,
      startDate,
      endDate,
      quarter: quarter + 1,
      year
    });
  }
  
  return periods;
}

/**
 * Format period for display
 */
export function formatPeriod(period: Period, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    return `الربع ${period.quarter} - ${period.year}`;
  }
  return `Q${period.quarter} ${period.year}`;
}

/**
 * Check if user can create new exam/questionnaire in current period
 */
export function canCreateNewRecord(lastRecordDate: Date | null): boolean {
  if (!lastRecordDate) return true;
  
  const currentPeriod = getCurrentPeriod();
  const lastRecordPeriod = getPeriodForDate(lastRecordDate);
  
  return currentPeriod.id !== lastRecordPeriod.id;
}

/**
 * Get the next available period
 */
export function getNextPeriod(currentPeriod: Period): Period {
  let nextQuarter = currentPeriod.quarter;
  let nextYear = currentPeriod.year;
  
  if (nextQuarter === 4) {
    nextQuarter = 1;
    nextYear += 1;
  } else {
    nextQuarter += 1;
  }
  
  const startMonth = (nextQuarter - 1) * 3;
  const startDate = new Date(nextYear, startMonth, 1);
  const endDate = new Date(nextYear, startMonth + 3, 0);
  const periodId = `${nextYear}-Q${nextQuarter}`;
  
  return {
    id: periodId,
    startDate,
    endDate,
    quarter: nextQuarter,
    year: nextYear
  };
}
